/**
 * BriefRegenerateActions (v1.8.0) — two buttons:
 *   - Regenerate brief: dispatches /regenerate-brief (text untouched).
 *   - Regenerate post: dispatches /regenerate-post (writer + brief + slots).
 *     Shows a confirm dialog warning that master regen marks derivatives stale.
 */

import React, { useState, useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

export interface BriefRegenerateActionsProps {
  onRegenerateBrief: () => Promise<void> | void;
  onRegeneratePost?: () => Promise<void> | void;
  busy?: boolean;
  isMaster?: boolean;
}

export const BriefRegenerateActions: React.FC<BriefRegenerateActionsProps> = ({
  onRegenerateBrief,
  onRegeneratePost,
  busy = false,
  isMaster = false,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleRegenPost = useCallback(() => {
    if (!onRegeneratePost) return;
    setConfirmOpen(false);
    void onRegeneratePost();
  }, [onRegeneratePost]);

  return (
    <>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            void onRegenerateBrief();
          }}
          disabled={busy}
          data-testid="design-brief-regenerate-brief-btn"
        >
          Regenerate brief
        </Button>
        {onRegeneratePost && (
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            data-testid="design-brief-regenerate-post-btn"
          >
            Regenerate post
          </Button>
        )}
      </Stack>
      <Dialog open={confirmOpen && Boolean(onRegeneratePost)} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Regenerate post?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will overwrite the writer text, brief, and asset slots.
            {isMaster ? ' Dependent derivative posts will be marked stale.' : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleRegenPost} color="warning" variant="contained">
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
